import { useEffect, useRef, useState, type ReactNode } from "react";
import "../styles/animations.css";

type Passion = {
  icon: string;
  name: string;
  description: ReactNode;
};

type Accent = {
  text: string;
  border: string;
  glow: string;
  dot: string;
};

const outdoorsAccent: Accent = {
  text: "text-sky-300",
  border: "hover:border-sky-400/40",
  glow: "group-hover:shadow-sky-500/10",
  dot: "bg-sky-400",
};

/** Mountains ridden, grouped by region. */
const snowMountains: { region: string; places: string[] }[] = [
  {
    region: "British Columbia",
    places: ["Whistler", "Cypress", "Grouse", "Seymour", "Apex", "Big White"],
  },
  { region: "USA", places: ["Mt. Baker", "Mammoth"] },
  {
    region: "Japan",
    places: ["Happo-One", "Goryu", "Shiga Kogen", "Tsugaike Kogen", "Hakuba Cortina"],
  },
];

/** Remaining outdoor activities (snowboarding + surfing are featured above). */
const outdoors: Passion[] = [
  {
    icon: "⛵",
    name: "Sailing",
    description: (
      <>
        Learned to sail on an{" "}
        <a
          href="https://www.rssailing.com/project/rs-zest/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-dotted underline-offset-2 hover:text-white transition-colors"
        >
          RS Zest
        </a>
        , the nimble little training dinghy I did my first runs on.
      </>
    ),
  },
  {
    icon: "🚵",
    name: "Mountain Biking",
    description: "On my hardtail I've ridden trails all across BC's Lower Mainland.",
  },
  {
    icon: "🛶",
    name: "Kayaking",
    description: "Calm mornings on the water, paddle in hand.",
  },
  {
    icon: "🏊",
    name: "Swimming",
    description:
      "A lifelong comfort in the water that underpins everything else I do out there.",
  },
  {
    icon: "🤿",
    name: "Scuba Diving",
    description: "Exploring what's going on beneath the surface.",
  },
  {
    icon: "🪁",
    name: "Kitesurfing",
    description: "Harnessing wind and water for one of the best rides going.",
  },
  {
    icon: "🏍️",
    name: "ATV",
    description: "Off the beaten path whenever the trail turns to dirt.",
  },
];

type Category = {
  title: string;
  blurb: string;
  accent: Accent;
  items: Passion[];
};

const otherCategories: Category[] = [
  {
    title: "Sports & Training",
    blurb: "Staying sharp, strong, and mobile.",
    accent: {
      text: "text-emerald-300",
      border: "hover:border-emerald-400/40",
      glow: "group-hover:shadow-emerald-500/10",
      dot: "bg-emerald-400",
    },
    items: [
      {
        icon: "🎾",
        name: "Tennis",
        description: "Baseline rallies and the occasional serve that actually lands.",
      },
      {
        icon: "⛳",
        name: "Golf",
        description: "Chasing a cleaner swing one round at a time.",
      },
      {
        icon: "🥊",
        name: "Kickboxing",
        description:
          "Pads, bags, and conditioning — my favourite way to blow off steam.",
      },
      {
        icon: "🤸",
        name: "Calisthenics",
        description: "Bodyweight strength training, anywhere, no equipment required.",
      },
      {
        icon: "🧘",
        name: "Yoga",
        description: "Mobility, balance, and a reset for the mind.",
      },
      {
        icon: "🧘‍♀️",
        name: "Pilates",
        description: "Core control and the small stabilizers everything else relies on.",
      },
    ],
  },
  {
    title: "The Arts",
    blurb: "Making music when I'm off the trail and away from the keyboard.",
    accent: {
      text: "text-violet-300",
      border: "hover:border-violet-400/40",
      glow: "group-hover:shadow-violet-500/10",
      dot: "bg-violet-400",
    },
    items: [
      {
        icon: "🎛️",
        name: "Music Production",
        description:
          "I produce mainly in Ableton Live, with time also logged in FL Studio, played in over the years through a range of Akai and Korg gear.",
      },
      {
        icon: "🎹",
        name: "Keyboard",
        description:
          "My daily driver is a full-size 88-key weighted Korg — the heart of my writing setup.",
      },
      {
        icon: "🎸",
        name: "Guitar",
        description: "Six strings on hand whenever a track asks for them.",
      },
    ],
  },
];

function PassionCard({ item, accent }: { item: Passion; accent: Accent }) {
  return (
    <article
      className={`tile glass-tile group relative flex flex-col gap-3 rounded-xl border border-white/10 bg-black/30 p-5 transition-all duration-300 hover:scale-[1.015] hover:shadow-lg ${accent.border} ${accent.glow}`}
    >
      <div className="flex items-center gap-3">
        <span className="text-3xl leading-none" aria-hidden="true">
          {item.icon}
        </span>
        <h3 className={`text-lg font-bold transition-colors ${accent.text}`}>
          {item.name}
        </h3>
      </div>
      <p className="text-sm leading-relaxed text-gray-300">{item.description}</p>
    </article>
  );
}

/** Responsive YouTube embed — vertical for Shorts, 16:9 otherwise. */
function YouTube({
  id,
  title,
  vertical = false,
}: {
  id: string;
  title: string;
  vertical?: boolean;
}) {
  return (
    <div
      className={`relative w-full overflow-hidden rounded-xl border border-white/10 bg-black ${
        vertical ? "aspect-[9/16]" : "aspect-video"
      }`}
    >
      <iframe
        src={`https://www.youtube.com/embed/${id}`}
        title={title}
        className="absolute inset-0 h-full w-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </div>
  );
}

/** Looping, muted, slow-motion video — a moving photo. */
function SlowLoopVideo({ src }: { src: string }) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const applyRate = () => {
    if (ref.current) ref.current.playbackRate = 0.5;
  };
  useEffect(applyRate, []);
  return (
    <video
      ref={ref}
      src={src}
      autoPlay
      muted
      loop
      playsInline
      onLoadedData={applyRate}
      onPlay={applyRate}
      className="mx-auto block max-h-[80vh] w-auto max-w-full"
    />
  );
}

function FeaturedSnowboarding() {
  return (
    <article className="rounded-2xl border border-white/10 bg-black/30 p-5 md:p-7">
      <div className="flex items-center gap-3">
        <span className="text-4xl leading-none" aria-hidden="true">
          🏂
        </span>
        <h3 className="text-2xl font-bold text-sky-300">Snowboarding</h3>
      </div>
      <p className="mt-3 max-w-3xl text-sm md:text-base leading-relaxed text-gray-300">
        Riding since 17, with home mountains at Whistler and around Vancouver.
        I've coached friends and family for years, ridden the resorts near Los
        Angeles, and made three trips to Japan — Nagano included — with Niseko up
        next this season.
      </p>

      {/* Mountains ridden */}
      <div className="mt-6">
        <p className="font-plex-mono text-xs uppercase tracking-[0.16em] text-sky-300/90 mb-3">
          Mountains Ridden
        </p>
        <div className="space-y-3">
          {snowMountains.map((group) => (
            <div key={group.region} className="flex flex-col sm:flex-row sm:items-center gap-x-3 gap-y-1.5">
              <span className="font-plex-mono text-[10px] uppercase tracking-[0.12em] text-gray-500 sm:w-36 shrink-0">
                {group.region}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {group.places.map((place) => (
                  <span
                    key={place}
                    className="text-[13px] px-2.5 py-1 rounded-full border border-white/15 text-gray-200 bg-white/[0.03]"
                  >
                    {place}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Japan photo */}
      <figure className="mt-6 relative overflow-hidden rounded-xl border border-white/10">
        <img
          src="/assets/passions/japan-snowboarding.jpg"
          alt="Riding the chairlift in Hakuba, Japan"
          loading="lazy"
          className="w-full aspect-video object-cover"
        />
        <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-4 py-3 text-sm text-gray-100">
          Hakuba, Japan
        </figcaption>
      </figure>

      {/* Clips */}
      <div className="mt-6">
        <p className="font-plex-mono text-xs uppercase tracking-[0.16em] text-sky-300/90 mb-3">
          Clips
        </p>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 min-w-0">
            <YouTube id="I7_HHNProQA" title="Snowboarding" />
          </div>
          <div className="w-full max-w-[280px] md:max-w-none md:w-[280px] shrink-0 mx-auto">
            <YouTube id="F1fYEwBKpck" title="Snowboarding (short)" vertical />
          </div>
        </div>
      </div>
    </article>
  );
}

function FeaturedSurfing() {
  return (
    <article className="overflow-hidden rounded-2xl border border-white/10 bg-black/30">
      <div className="p-5 md:p-7 pb-4">
        <div className="flex items-center gap-3">
          <span className="text-4xl leading-none" aria-hidden="true">
            🏄
          </span>
          <h3 className="text-2xl font-bold text-sky-300">Surfing</h3>
        </div>
        <p className="mt-3 max-w-3xl text-sm md:text-base leading-relaxed text-gray-300">
          Paddling out wherever the coast delivers a swell — including plenty of
          sessions in Taiwan.
        </p>
      </div>
      <div className="relative flex justify-center bg-black">
        <SlowLoopVideo src="/assets/passions/surfing-taiwan.mp4" />
        <span className="pointer-events-none absolute bottom-3 left-4 text-xs font-plex-mono uppercase tracking-[0.14em] text-white/80 drop-shadow">
          Taiwan
        </span>
      </div>
    </article>
  );
}

function Passions() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <main
      className="flex-1 w-full px-4 md:px-8 py-12 max-w-6xl mx-auto"
      style={{ animation: isLoaded ? "fadeIn 0.6s ease-out" : undefined }}
    >
      {/* Hero */}
      <header
        className="relative overflow-hidden rounded-2xl border border-white/15 mb-12"
        style={{ animation: "cascadeIn 0.6s ease-out 0.1s backwards" }}
      >
        <img
          src="/assets/tiles/passions.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: "center center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
        <div className="relative z-10 px-6 md:px-10 py-16 md:py-24">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white drop-shadow">
            Passions &amp; Hobbies
          </h1>
          <p className="mt-4 max-w-2xl text-base md:text-lg text-gray-200 leading-relaxed drop-shadow">
            Life outside of work — the mountains and the water, staying in
            fighting shape, and making music.
          </p>
        </div>
      </header>

      {/* The Outdoors */}
      <section
        className="mb-14"
        style={{ animation: "cascadeIn 0.6s ease-out 0.2s backwards" }}
      >
        <div className="flex items-center gap-3 mb-1">
          <span className={`h-2.5 w-2.5 rounded-full ${outdoorsAccent.dot}`} />
          <h2 className="text-2xl md:text-3xl font-bold text-white">The Outdoors</h2>
        </div>
        <p className="text-sm text-gray-400 mb-6 ml-6">
          Mountains, water, and trails — where I spend most of my free time.
        </p>

        <div className="space-y-5">
          <FeaturedSnowboarding />
          <FeaturedSurfing />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {outdoors.map((item) => (
              <PassionCard key={item.name} item={item} accent={outdoorsAccent} />
            ))}
          </div>
        </div>
      </section>

      {/* Sports & Arts */}
      <div className="space-y-14">
        {otherCategories.map((category, ci) => (
          <section
            key={category.title}
            style={{ animation: `cascadeIn 0.6s ease-out ${0.3 + ci * 0.1}s backwards` }}
          >
            <div className="flex items-center gap-3 mb-1">
              <span className={`h-2.5 w-2.5 rounded-full ${category.accent.dot}`} />
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                {category.title}
              </h2>
            </div>
            <p className="text-sm text-gray-400 mb-6 ml-6">{category.blurb}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {category.items.map((item) => (
                <PassionCard key={item.name} item={item} accent={category.accent} />
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-16 text-center text-gray-500 text-sm border-t border-white/10 pt-8">
        <p>© {new Date().getFullYear()} Walt Yao. All rights reserved.</p>
      </div>
    </main>
  );
}

export default Passions;
